import React, { FormEvent, useEffect, useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios, { apiBaseUrl } from '../api/httpClient';
import { useAuth } from '../auth/AuthContext';
import { safeReturnTo } from '../auth/safeReturnTo';

const LoginPage: React.FC = () => {
  const { user, login, completeMicrosoftLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMicrosoftSubmitting, setIsMicrosoftSubmitting] = useState(false);
  const [microsoftReturnTo, setMicrosoftReturnTo] = useState<string | null>(null);
  const [isMicrosoftAvailable, setIsMicrosoftAvailable] = useState(false);
  const [isPasswordLoginEnabled, setIsPasswordLoginEnabled] = useState(false);
  const [isPolicyLoading, setIsPolicyLoading] = useState(true);
  const [isPolicyLoadFailed, setIsPolicyLoadFailed] = useState(false);
  const [policyRefresh, setPolicyRefresh] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
  const from = safeReturnTo(typeof fromState?.pathname === 'string'
    ? fromState.pathname
      + (typeof fromState.search === 'string' && fromState.search.startsWith('?') ? fromState.search : '')
      + (typeof fromState.hash === 'string' && fromState.hash.startsWith('#') ? fromState.hash : '')
    : undefined);

  useEffect(() => {
    let isMounted = true;
    setIsPolicyLoading(true);
    setIsPolicyLoadFailed(false);
    setIsPasswordLoginEnabled(false);
    setIsMicrosoftAvailable(false);

    if (!apiBaseUrl) {
      setIsPolicyLoading(false);
      setIsPolicyLoadFailed(true);
      return () => {
        isMounted = false;
      };
    }

    axios.get<{ enabled: boolean; passwordLoginEnabled?: boolean }>(`${apiBaseUrl}/auth/microsoft/status`)
      .then((response) => {
        if (typeof response.data.enabled !== 'boolean'
          || (response.data.passwordLoginEnabled !== undefined
            && typeof response.data.passwordLoginEnabled !== 'boolean')) {
          throw new Error('Invalid sign-in policy response.');
        }
        if (isMounted) {
          setIsMicrosoftAvailable(response.data.enabled);
          // A successful legacy API response predates the staged server gate.
          // Failed/unknown responses must never silently offer a password bypass.
          setIsPasswordLoginEnabled(response.data.passwordLoginEnabled !== false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsMicrosoftAvailable(false);
          setIsPasswordLoginEnabled(false);
          setIsPolicyLoadFailed(true);
        }
      })
      .finally(() => {
        if (isMounted) setIsPolicyLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [policyRefresh]);

  useEffect(() => {
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.substring(1)
      : window.location.hash;

    if (!hash) {
      return;
    }

    const params = new URLSearchParams(hash);
    const microsoftToken = params.get('microsoft_token');
    const microsoftError = params.get('microsoft_error');
    const returnTo = safeReturnTo(params.get('return_to') ?? from);

    if (!microsoftToken && !microsoftError) {
      return;
    }

    window.history.replaceState(null, document.title, `${window.location.pathname}${window.location.search}`);

    if (microsoftError) {
      setError(microsoftError);
      return;
    }

    if (!microsoftToken) {
      return;
    }

    setError(null);
    setMicrosoftReturnTo(returnTo);
    setIsMicrosoftSubmitting(true);

    completeMicrosoftLogin(microsoftToken)
      .then(() => navigate(returnTo, { replace: true }))
      .catch(() => setError('Microsoft sign-in was accepted, but GCM could not start a session.'))
      .finally(() => setIsMicrosoftSubmitting(false));
  }, [completeMicrosoftLogin, from, navigate]);

  if (user && !isMicrosoftSubmitting) {
    // Router transitions can finish after the session/user update. Both navigation
    // paths must use the same validated callback target, not race back to '/'.
    return <Navigate to={microsoftReturnTo ?? from} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (isPolicyLoading || !isPasswordLoginEnabled) {
      setError('Password sign-in is disabled. Use Microsoft sign-in.');
      return;
    }
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (failure) {
      const code = (failure as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code === 'password_login_disabled') {
        setIsPasswordLoginEnabled(false);
        setError('Password sign-in is disabled. Use Microsoft sign-in.');
      } else {
        setError('The email address or password was not accepted.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMicrosoftSignIn = () => {
    setError(null);

    if (!apiBaseUrl) {
      setError('GCM API URL is not configured.');
      return;
    }

    setIsMicrosoftSubmitting(true);
    window.location.assign(`${apiBaseUrl}/auth/microsoft/redirect?return_to=${encodeURIComponent(safeReturnTo(from))}`);
  };

  return (
    <Container className="login-page">
      <Card className="login-panel border-0 shadow-sm">
        <Card.Body>
          <p className="login-panel__eyebrow">Gemstone Operations</p>
          <h1>Sign in to GCM</h1>
          {error && <Alert variant="danger">{error}</Alert>}
          {isPolicyLoading && (
            <div role="status"><Spinner animation="border" size="sm" className="me-2" />Checking sign-in options…</div>
          )}
          {isPolicyLoadFailed && (
            <Alert variant="warning">
              <p>Sign-in options could not be loaded. Retry or contact your administrator.</p>
              <Button variant="outline-secondary" onClick={() => setPolicyRefresh((value) => value + 1)}>
                Retry sign-in options
              </Button>
            </Alert>
          )}
          {!isPolicyLoading && !isPolicyLoadFailed && !isPasswordLoginEnabled && (
            <Alert variant={isMicrosoftAvailable ? 'info' : 'danger'}>
              {isMicrosoftAvailable
                ? 'Use your Gemstone Microsoft account to sign in. Password sign-in is disabled.'
                : 'Sign-in is currently unavailable. Contact your administrator.'}
            </Alert>
          )}
          {isMicrosoftAvailable && (
            <>
              <Button
                className="login-panel__microsoft-button"
                disabled={isSubmitting || isMicrosoftSubmitting}
                onClick={handleMicrosoftSignIn}
                type="button"
                variant="outline-primary"
              >
                {isMicrosoftSubmitting && <Spinner animation="border" size="sm" className="me-2" />}
                Sign in with Microsoft
              </Button>
              {isPasswordLoginEnabled && (
                <div className="login-panel__divider">
                  <span>or use password</span>
                </div>
              )}
            </>
          )}
          {isPasswordLoginEnabled && <Form onSubmit={handleSubmit}>
            <Form.Group controlId="gcm-login-email">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                autoComplete="email"
                disabled={isSubmitting || isMicrosoftSubmitting}
                onChange={(event) => setEmail(event.target.value)}
                required
                type="email"
                value={email}
              />
            </Form.Group>
            <Form.Group controlId="gcm-login-password">
              <Form.Label>Password</Form.Label>
              <Form.Control
                autoComplete="current-password"
                disabled={isSubmitting || isMicrosoftSubmitting}
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
            </Form.Group>
            <Button disabled={isSubmitting || isMicrosoftSubmitting} type="submit">
              {isSubmitting && <Spinner animation="border" size="sm" className="me-2" />}
              Sign in
            </Button>
          </Form>}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default LoginPage;
